import { useEffect, useMemo, useState } from 'react'
import { calculateFeed, calculateMarks } from '../domain/economy'
import { formatClock, remainingSeconds } from '../domain/timer'
import { isGoogleConnected } from '../services/googleTasks'
import { useAppStore } from '../store'
import type { GoogleTaskRef, SessionMode } from '../types'
import { Aquarium } from '../components/Aquarium'
import { GoogleTaskPicker } from '../components/GoogleTaskPicker'
import { Icon } from '../components/Icons'
import { useAuth } from '../auth'

const presets = [10, 25, 45, 50]

function CompletionPanel() {
  const { activeSession, state, dismissCompletion, syncTaskLogs } = useAppStore()
  useEffect(() => {
    if (activeSession?.status === 'completed' && activeSession.googleTask && isGoogleConnected()) void syncTaskLogs()
  }, [activeSession?.status, activeSession?.id, activeSession?.googleTask, syncTaskLogs])
  if (!activeSession) return null

  const completed = activeSession.status === 'completed'
  return (
    <section className="focus-active completion-screen">
      <Aquarium mode={completed ? 'completion' : 'idle'} />
      <div className="focus-overlay completion-overlay">
        <div className="completion-symbol">{completed ? <Icon name="check" /> : <span>—</span>}</div>
        <p className="eyebrow">{completed ? 'Focus complete' : 'Session ended early'}</p>
        <h1>{completed ? 'Your effort became something living.' : 'Your cove is safe.'}</h1>
        <p className="focus-task">{activeSession.title}</p>
        {completed ? (
          <div className="reward-summary">
            <span><Icon name="focus" /><b>{activeSession.plannedMinutes}</b> minutes</span>
            <span><Icon name="feed" /><b>+{activeSession.feedEarned}</b> feed</span>
            <span><Icon name="sparkle" /><b>+{activeSession.marksEarned}</b> marks</span>
          </div>
        ) : <p>This partial session stays in your history without a Discovery reward.</p>}
        {activeSession.googleTask && <p className="sync-message">{state.pendingTaskLogs.some((entry) => entry.sessionId === activeSession.id) ? 'Saved — your Google Task will update when connected.' : 'Google Task details updated.'}</p>}
        <button className="primary large" onClick={dismissCompletion}>{completed ? 'Return to your cove' : 'Start again when ready'}</button>
      </div>
    </section>
  )
}

function ActiveTimer() {
  const { user } = useAuth()
  const { activeSession, state, pauseActive, resumeActive, endActive, completeActive, adminCompleteActive } = useAppStore()
  const [now, setNow] = useState(Date.now())
  const [confirmStop, setConfirmStop] = useState(false)
  const remaining = activeSession ? remainingSeconds(activeSession, now) : 0

  useEffect(() => {
    if (!activeSession || !['running', 'paused'].includes(activeSession.status)) return
    const interval = window.setInterval(() => setNow(Date.now()), 500)
    return () => window.clearInterval(interval)
  }, [activeSession])

  useEffect(() => {
    if (activeSession?.status === 'running' && remaining <= 0) {
      completeActive()
      if (state.settings.notifications && Notification.permission === 'granted') {
        new Notification('Focus complete', { body: 'Your cove grew with the time you protected.' })
      }
    }
  }, [remaining, activeSession?.status, completeActive, state.settings.notifications])

  useEffect(() => {
    const refresh = () => setNow(Date.now())
    document.addEventListener('visibilitychange', refresh)
    window.addEventListener('focus', refresh)
    return () => { document.removeEventListener('visibilitychange', refresh); window.removeEventListener('focus', refresh) }
  }, [])

  if (!activeSession) return null
  if (['completed', 'ended_early'].includes(activeSession.status)) return <CompletionPanel />
  const paused = activeSession.status === 'paused'
  return (
    <section className="focus-active">
      <Aquarium mode={paused ? 'paused' : 'focus'} />
      <div className="focus-overlay">
        <div className="live-chip"><i /> {paused ? 'Paused' : 'Focus in progress'}</div>
        <h1 className="timer-display" aria-live="polite">{formatClock(remaining)}</h1>
        <p className="focus-task">{activeSession.title}</p>
        <p className="focus-note">{paused ? 'Take the moment you need. Resume when ready.' : 'Your cove is calm. Keep going.'}</p>
        <div className="timer-controls">
          <button className="round secondary" onClick={paused ? resumeActive : pauseActive} disabled={activeSession.mode === 'strict'} aria-label={paused ? 'Resume session' : 'Pause session'}><Icon name={paused ? 'play' : 'pause'} /></button>
          <button className="text-button light" onClick={() => setConfirmStop(true)}>End session</button>
          {user.isAdmin && (
            <button className="admin-complete-button" onClick={adminCompleteActive}>
              Admin: Finish now + full rewards
            </button>
          )}
        </div>
      </div>
      {confirmStop && (
        <div className="modal-backdrop"><section className="modal-card compact-modal" role="dialog" aria-modal="true"><h2>End this session?</h2><p>It will stay in your history. No fish or progress will be harmed.</p><button className="primary wide" onClick={() => setConfirmStop(false)}>Continue focusing</button><button className="danger-quiet wide" onClick={endActive}>End without reward</button></section></div>
      )}
    </section>
  )
}

export function FocusView() {
  const { activeSession, startSession } = useAppStore()
  const [task, setTask] = useState('')
  const [googleTask, setGoogleTask] = useState<GoogleTaskRef>()
  const [minutes, setMinutes] = useState(25)
  const [custom, setCustom] = useState(false)
  const [mode, setMode] = useState<SessionMode>('flexible')
  const reward = useMemo(() => ({ feed: calculateFeed(minutes), marks: calculateMarks(minutes) }), [minutes])

  if (activeSession) return <ActiveTimer />

  return (
    <main className="view focus-setup-view">
      <header className="view-header"><div><p className="eyebrow">One clear commitment</p><h1>Start a focus session</h1></div><span className="calm-badge">Default: flexible</span></header>
      <div className="setup-layout">
        <section className="panel setup-card">
          <label className="field-label">What will you work on?
            <input value={googleTask?.title ?? task} onChange={(event) => { setGoogleTask(undefined); setTask(event.target.value) }} placeholder="Write one clear task" autoFocus />
          </label>
          <GoogleTaskPicker value={googleTask} onChange={(selected) => { setGoogleTask(selected); if (selected) setTask(selected.title) }} />
          <fieldset><legend>Focus duration</legend><div className="preset-row">{presets.map((value) => <button key={value} className={minutes === value && !custom ? 'selected' : ''} onClick={() => { setMinutes(value); setCustom(false) }}>{value}<small>min</small></button>)}<button className={custom ? 'selected' : ''} onClick={() => setCustom(true)}>Custom</button></div></fieldset>
          {custom && <label className="field-label">Custom minutes<input type="number" min="5" max="120" value={minutes} onChange={(event) => setMinutes(Math.max(5, Math.min(120, Number(event.target.value))))} /></label>}
          <fieldset><legend>Session style</legend><div className="mode-grid">{(['flexible', 'committed', 'strict'] as SessionMode[]).map((value) => <button key={value} className={mode === value ? 'selected' : ''} onClick={() => setMode(value)}><strong>{value[0].toUpperCase() + value.slice(1)}</strong><small>{value === 'flexible' ? 'Pause freely' : value === 'committed' ? 'One mindful pause' : 'No pause'}</small></button>)}</div></fieldset>
          <div className="reward-estimate"><span><Icon name="feed" /> About {reward.feed} feed</span><span><Icon name="sparkle" /> {reward.marks} Discovery Marks</span></div>
          <button className="primary large wide" onClick={() => startSession(googleTask?.title ?? task, minutes, mode, googleTask)}><Icon name="play" /> Start focus</button>
          <p className="fine-print">Your timer is saved before it starts and recalculates from timestamps after refresh or backgrounding.</p>
        </section>
        <aside className="setup-preview"><Aquarium mode="focus" compact /><blockquote>“Make starting feel safe and achievable.”</blockquote></aside>
      </div>
    </main>
  )
}
